import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { getTime } from "../../../../services/projects-services";
import { fetchFilteredProjectsGroups } from "../../projects.utils";
import { fetchCompanies } from "../../../../services/companiesServices";
import { fetchLinkedProjects } from "../../../../services/projectsServices";
import Loader from "../../../../components/loader/loader";
import classNames from "classnames";
import Table from "../../../../components/table/table";
import ProjectGroupColumns from "./ProjectGroupColumns";
import CreateProjectModal from "../../create-project-modal/create-project-modal";
import FilterComponent from "../../../../components/filter/filter";
import { useGetProjectGroupOptions } from "../../../../components/filter/filter.utils";
import { parsedFilterData } from "./project-group-list.utils";
import { useAuth } from "../../../../context/AuthContext";
import SearchHeader from "../../../../components/search-header/search-header";
import { ProjectTableStyled } from "./ProjectTable.styles";
import { format, parse } from "date-fns";
import { generateProjectGroupCSV } from "./csv";

const parseStartDateCreated = (dateString) => {
  const input = dateString; // DD/MM/YYYY
  const parsedDate = parse(input, "dd/MM/yyyy", new Date());
  if (isNaN(parsedDate)) {
    return null; // Handle invalid date
  }
  const output = format(parsedDate, "MM/dd/yyyy");

  return output;
};

function ProjectGroupList() {
  const { userPermissions } = useAuth();
  const [showModal, setShowModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [nextId, setNextId] = useState(null);
  const [filterState, setFilterState] = useState([]);
  const navigate = useNavigate();

  // Query: Users
  const { data: users, isLoading: isUsersListLoading } = useQuery({
    queryKey: ["users"],
    queryFn: getTime,
  });

  // Query: Project Group Options for Filter
  const { options: projGroupOption, isLoading: isProjGroupOptionsLoading } =
    useGetProjectGroupOptions();

  // Query: Project Groups
  const {
    data: groupsList,
    refetch,
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: ["project-groups", filterState],
    queryFn: () => fetchFilteredProjectsGroups(filterState),
    refetchOnWindowFocus: false,
  });

  const projectGroups = useMemo(() => {
    if (!groupsList) return [];
    return groupsList.map((group) => ({
      ...group,
      dateCreated: group.dateCreated
        ? parseStartDateCreated(group.dateCreated)
        : null,
    }));
  }, [groupsList]);

  // Query: Companies
  const { data: companies, isLoading: isCompaniesLoading } = useQuery({
    queryKey: ["companies"],
    queryFn: fetchCompanies,
    select: (data) => {
      const companyMap = {};
      data.forEach((company) => {
        companyMap[company.companyID] = company.companyName;
      });
      return companyMap;
    },
  });

  const { data: linkedProjectsData, isLoading: isLinkedProjectsLoading } =
    useQuery({
      queryKey: ["linked-projects", projectGroups],
      queryFn: async () => {
        if (!projectGroups) return [];
        const results = await Promise.all(
          projectGroups.map((group) =>
            fetchLinkedProjects(group.firestoreId).then((data) => ({
              groupId: group.firestoreId,
              projects: data,
            }))
          )
        );
        return results;
      },
      enabled: !!projectGroups?.length,
      refetchOnWindowFocus: false,
    });

  // Combined loading flag: Check if any query is still loading
  const isAnyLoading =
    isUsersListLoading ||
    isProjGroupOptionsLoading ||
    isCompaniesLoading ||
    isLoading ||
    isFetching ||
    isLinkedProjectsLoading;

  // Compute aggregated values for each project group
  const projectGroupData = useMemo(() => {
    return (
      projectGroups?.map((group) => {
        const linkedProjects =
          linkedProjectsData?.find((proj) => proj.groupId === group.firestoreId)
            ?.projects || [];

        let plannerServiceValue = 0;
        let projectTotal = 0;
        let materialValue = 0;
        let invoiceValue = 0;

        linkedProjects.forEach((project) => {
          const cleanInvoiceValue = String(project.invoiceValue || "0").replace(
            /[^0-9.-]+/g,
            ""
          );

          plannerServiceValue += parseFloat(project.plannerServiceValue || 0);
          projectTotal += parseFloat(project.projectTotal || 0);
          invoiceValue += parseFloat(cleanInvoiceValue);
          materialValue += parseFloat(
            project?.materials?.reduce(
              (sum, mat) => sum + parseFloat(mat.total || 0),
              0
            )
          );
        });

        return {
          ...group,
          linkedProjects, // Pass linked projects to parsedFilterData
          plannerServiceValue,
          projectTotal,
          materialValue,
          invoiceValue,
        };
      }) || []
    );
  }, [projectGroups, linkedProjectsData]);

  // Filter projects based on search query
  const searchFilteredProjects = useMemo(() => {
    const query = searchQuery?.toLowerCase().trim() || "";
    return projectGroupData?.filter((group) => {
      const clientName = companies?.[group.client] || "";
      return (
        !query ||
        Object.values(group).some((value) =>
          value?.toString().toLowerCase().includes(query)
        ) ||
        clientName.includes(query)
      );
    });
  }, [projectGroupData, searchQuery, companies]);

  // Parse filtered data for table
  const tableData = useMemo(
    () => parsedFilterData(searchFilteredProjects, users),
    [searchFilteredProjects, users]
  );

  const handleSearch = (query) => setSearchQuery(query);

  const columns = useMemo(
    () => ProjectGroupColumns(companies, navigate),
    [companies, navigate]
  );

  const showHeaderCta = useMemo(
    () =>
      userPermissions === "Admin" ||
      userPermissions === "Project Manager" ||
      userPermissions === "Supervisor",
    [userPermissions]
  );

  const downloadProjectsCsv = async () => {
    const csv = await generateProjectGroupCSV(tableData, companies);

    if (!csv) {
      alert("No data available to export.");
      return;
    }

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.setAttribute("download", "projects-group-list.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const headerOptions = [
    {
      title: `Export List CSV`,
      onClick: () => {
        downloadProjectsCsv();
      },
    },
  ];

  return (
    <ProjectTableStyled>
      <SearchHeader
        placeholder={"Search a keyword, client or project."}
        heading={"Project Groups"}
        ctaTitle={showHeaderCta ? "Add Project Group" : ""}
        ctaHandler={() => setShowModal(true)}
        handleSearch={handleSearch}
        optionsList={headerOptions}
      />
      <div className="projects-table">
        <FilterComponent
          label="Filter Project Groups"
          options={projGroupOption}
          state={filterState}
          setState={setFilterState}
          isLoading={isProjGroupOptionsLoading}
        />

        <>
          {/* Render loader if any query is still loading  */}
          {isAnyLoading && <Loader absolute />}

          {/* Render table only when everything has loaded */}
          {!isAnyLoading && (
            <div
              className={classNames({
                "table-wrapper": true,
                "full-height": tableData.length > 0,
              })}
            >
              <Table
                data={tableData}
                columns={columns}
                isLoading={isUsersListLoading}
                noDataMessage={"No grouped projects available"}
                sortable
                stickyHeader={true}
                rowBackground="white"
                rowHover={true}
                useUniformBackground={true}
              />
              {showModal && (
                <CreateProjectModal
                  nextId={nextId}
                  onClose={() => setShowModal(false)}
                  onSuccess={() => {
                    refetch(); // Refetch projects to update the list
                    setShowModal(false);
                  }}
                  isConfirming={false}
                  projectGroupOptions={projectGroups}
                  isCreationGroupView={true}
                />
              )}
            </div>
          )}
        </>
      </div>
    </ProjectTableStyled>
  );
}

export default React.memo(ProjectGroupList);
